/**
 * Assistant Hooks
 *
 * Provides hooks for assistant CRUD operations using createResourceHooks.
 * Includes additional custom hooks for search, defaults, etc.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createResourceHooks } from './createResourceHooks'
import { raClient, type RAListParams } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'
import type {
  Assistant,
  AssistantListFilters,
  CreateAssistantInput,
  UpdateAssistantInput,
} from '@/types/assistant'
import { api } from '@/lib/api'

// ============================================================================
// Query Keys
// ============================================================================

export const assistantKeys = createQueryKeys('assistants')

// ============================================================================
// Base CRUD Hooks (via createResourceHooks)
// ============================================================================

const assistantHooks = createResourceHooks<Assistant>('assistants', assistantKeys)

// Re-export with more descriptive names
export const useAssistantsList = (
  params?: RAListParams & { filters?: AssistantListFilters },
  options?: { enabled?: boolean; staleTime?: number }
) => {
  // Build filter from AssistantListFilters
  const filter: Record<string, unknown> = { ...params?.filter }
  if (params?.filters?.customer_id?.length) {
    filter.customer_id = params.filters.customer_id
  }
  if (params?.filters?.q) {
    filter.q = params.filters.q
  }
  if (params?.filters?.is_active !== undefined) {
    filter.is_active = params.filters.is_active
  }

  return assistantHooks.useList(
    {
      page: params?.page,
      perPage: params?.perPage,
      sort: params?.sort ?? { field: 'updated_at', order: 'DESC' },
      filter,
    },
    options
  )
}

export const useAssistant = assistantHooks.useOne

export const useCreateAssistant = () => {
  const queryClient = useQueryClient()
  const mutation = assistantHooks.useCreate()

  return {
    ...mutation,
    mutateAsync: async (data: CreateAssistantInput) => {
      const result = await mutation.mutateAsync(data as Partial<Assistant>)
      // Invalidate ALL assistant queries to ensure lists and related queries are refreshed
      await queryClient.invalidateQueries({ queryKey: assistantKeys.all })
      return result
    },
    mutate: (data: CreateAssistantInput) => mutation.mutate(data as Partial<Assistant>),
  }
}

export const useUpdateAssistant = () => {
  const queryClient = useQueryClient()
  const mutation = assistantHooks.useUpdate()

  return {
    ...mutation,
    mutateAsync: async (data: UpdateAssistantInput) => {
      const result = await mutation.mutateAsync({ id: data.id, data })
      // Invalidate ALL assistant queries to prevent stale cache issues
      // This handles both string and number ID mismatches and related queries like 'simple'
      await queryClient.invalidateQueries({ queryKey: assistantKeys.all })
      return result
    },
    mutate: (data: UpdateAssistantInput) => mutation.mutate({ id: data.id, data }),
  }
}

export const useDeleteAssistant = () => {
  const queryClient = useQueryClient()
  const mutation = assistantHooks.useDelete()

  return {
    ...mutation,
    mutateAsync: async (id: number) => {
      const result = await mutation.mutateAsync(id)
      // Invalidate ALL assistant queries to ensure lists and related queries are refreshed
      await queryClient.invalidateQueries({ queryKey: assistantKeys.all })
      return result
    },
  }
}

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * Hook to get simple list of all assistants (for dropdowns, etc.)
 * Fetches all assistants without pagination
 */
export function useAssistantsSimple(enabled = true) {
  return useQuery({
    queryKey: [...assistantKeys.all, 'simple'],
    queryFn: async () => {
      const result = await raClient.getList<Assistant>('assistants', {
        page: 1,
        perPage: 999,
        sort: { field: 'name', order: 'ASC' },
      })
      return result.data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Alias for useAssistantsSimple for backward compatibility
 */
export const useAssistants = useAssistantsSimple

/**
 * Hook to search assistants with real-time autocomplete
 */
export function useSearchAssistants(searchTerm: string, enabled = true) {
  return useQuery({
    queryKey: [...assistantKeys.all, 'search', searchTerm],
    queryFn: async () => {
      const result = await raClient.getList<Assistant>('assistants', {
        page: 1,
        perPage: 50,
        sort: { field: 'name', order: 'ASC' },
        filter: searchTerm ? { q: searchTerm } : {},
      })
      return result.data
    },
    enabled,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to get specific assistants by IDs
 */
export function useAssistantsByIds(ids: number[], enabled = true) {
  return useQuery({
    queryKey: assistantKeys.byIds(ids),
    queryFn: () => raClient.getMany<Assistant>('assistants', ids),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch default values for creating a new assistant
 */
export function useAssistantDefaults(enabled = true) {
  return useQuery({
    queryKey: [...assistantKeys.all, 'defaults'],
    queryFn: async (): Promise<Partial<Assistant>> => {
      const response = await api.post('/assistants/ra/getDefaults')
      const data = response.data.data || response.data

      // Set dynamic greeting as default for new assistants
      return {
        ...data,
        name: data.name || '',
        config: {
          ...data.config,
          greetings: {
            ...data.config?.greetings,
            dynamic_enabled: true,
            style: data.config?.greetings?.style || 'simple_direct',
          },
        },
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}
